(ns arthur.domain.symbol
  "A SYMBOL: an ordered bag of nodes in its own frame space, and the two ways to
  evaluate it at a frame.

      {:id :main :frames 229 :nodes {id -> node} :palette nil}

  That is the whole type, and EVERYTHING THAT HOLDS NODES IS ONE OF THESE. What
  a document opens on is a symbol; what a `:kind :instance` node places is a
  symbol; there is no second structure. An earlier arrangement had a root node
  tree and a library entry as two structures with the same fields and never said
  they were the same thing. Flash's `_root` is a MovieClip and After Effects'
  pre-comp is just a layer; collapsing them is what makes nesting arbitrary and
  free rather than a feature to be added.

  The clip-level facts are in `arthur.domain.clip`. A symbol has a FRAME SPACE,
  not a rate and not a size: `:fps` is the clip's, because a rate is a fact about
  how fast the whole thing plays, and a nested symbol cannot have its own.

  TWO AXES OF NESTING, and conflating them is why \"nested\" and \"flat with parent
  pointers\" sound contradictory when they are not. Parent/child is transform
  composition WITHIN one symbol and is stored flat with pointers. Instance is a
  symbol inside another symbol and is stored by reference into the library.
  Each symbol is flat; symbols nest. Every argument for flat storage —
  addressability, one-field reparenting, structural sharing, per-node sync leaves —
  is about the first axis and is untouched by the second.

  Two ways to evaluate one at a frame:

    (eval-frame sym f store palette opts)
                                 THE SPECIFICATION. Allocating, order-free,
                                 obviously correct. Use it in tests and for a
                                 one-off render.

    (resolver sym store palette opts)
                                 -> (fn [f] ops). What playback uses. Caches the
                                 topological order and the z paths, holds one
                                 CURSOR per channel and one PREALLOCATED point
                                 buffer per node, so a frame allocates the op
                                 maps and nothing else.

  Both run the same walk — `eval-into` below — parameterised by how a channel is
  read and where points are written. That is deliberate: two independent
  implementations of frame evaluation would drift, and the drift would look like
  a rendering bug rather than like two functions disagreeing. What differs
  between them is exactly the part that can be wrong, and symbol-test asserts
  they agree frame for frame in forward, backward and random order.

  The output is a list of DRAW OPS, and it is the boundary with the rasteriser:
  ops carry palette indices and raster-space points, and the rasteriser knows
  nothing about nodes, channels or time.

  Geometry is stored FLAT — [x0 y0 x1 y1 …] — in authored channels as well as
  dense ones. A dense block is a rectangular Int16Array and an authored ring is a
  vector of numbers, and they read the same way, which is what makes freezing
  fill in the same channel rather than convert into a second format."
  (:require [arthur.domain.channel :as ch]
            [arthur.domain.node :as node]
            [arthur.domain.pose :as pose]
            [arthur.domain.palette :as pal]))

;; ---------------------------------------------------------------------------
;; structure: depth, topological order, draw order

(defn lineage
  "The node's id and every ancestor's, nearest first and root last.

  One walk, shared by `depth` and `z-path`, which otherwise duplicate it.

  A cycle is caught by LENGTH rather than by a `seen` set: a chain that does not
  repeat cannot be longer than the number of nodes, so one step past that is
  proof of a loop and needs no bookkeeping. Caught rather than hung — a cycle is
  reachable from one bad `:node/set-parent`, and a hung tab is a far worse
  diagnostic than a stack trace naming the nodes.

  THE PROOF ONLY HOLDS FOR A NODE THIS SYMBOL HAS. An id that is not in `nodes`
  contributes a step the count knows nothing about, and its lineage is just
  itself; in an EMPTY symbol that one step used to be read as a loop, so asking
  where a node of another symbol sits threw `parent cycle` instead of answering
  that it sits nowhere."
  [nodes id]
  (let [up    (fn [i]
                (when-let [p (:parent (get nodes i))]
                  (if (contains? nodes p)
                    p
                    (throw (ex-info "node's :parent is not in the symbol"
                                    {:node i :parent p})))))
        chain (into [] (comp (take-while some?) (take (inc (count nodes))))
                    (iterate up id))]
    (when (and (contains? nodes id) (> (count chain) (count nodes)))
      (throw (ex-info "parent cycle in symbol" {:node id :chain chain})))
    chain))

(defn depth
  "Number of ancestors."
  [nodes id]
  (dec (count (lineage nodes id))))

(defn children
  "The symbol's own clips — the nodes placed directly in it — in timeline order.

  THE SYMBOL IS THE CONTAINER. There is no lane node to ask for its members: a
  symbol drawn as a lane draws THESE, and the sequence commands re-span THESE.
  See `docs/lane-is-a-view-plan.md`.

  Sorted by where they START, not by `:z`: blocks in a sequence follow one
  another in time, and two of them cannot be in the same place for `:z` to
  decide between. Ties go to the id so the order is the same on every run.

  A NODE WITH NO SPAN IS NOT IN THE SEQUENCE. A shape on screen for the whole
  shot has no `[in out)` to follow anything else, so it is not something an edge
  edit can trim or ripple, and a command that destructured its nil span would
  fail on the most ordinary node there is."
  [nodes]
  (->> (vals nodes)
       (filter #(and (nil? (:parent %)) (node/placed-span %)))
       (sort-by (juxt #(first (node/placed-span %)) #(str (:id %))))
       vec))

(defn frame-map
  "Node `id`'s own frames as a map from the containing symbol's, inverted:
  `{:at a :rate r}`, meaning symbol frame `p` is frame `r·(p − a)` of `id`.
  Identity for `nil`, which is a node sitting directly in the symbol.

  THE FRAME SPACE A COMMAND IS GIVEN ITS COORDINATE IN. A direct child reads
  the symbol's own frames. A node grouped beneath another composes through that
  parent chain. One rule either way, so a caller holding a node does not have to
  ask what it is sitting in or how the timeline happens to draw the symbol.

  Refuses floors and loops rather than pretend an affine map preserves them:
  through either, one frame of the symbol is not one frame of `id` and a command
  handed a single frame has no single answer to give."
  [nodes id]
  (loop [id id seen #{} chain []]
    (if (nil? id)
      (reduce node/then-time {:at 0 :rate 1} (map node/time-of (reverse chain)))
      (let [n (get nodes id) t (:time n)]
        (when (and n (not (contains? seen id))
                   (not (:loop? t))
                   (<= (or (:expose t) 1) 1)
                   (empty? (:holds t)))
          (recur (:parent n) (conj seen id) (conj chain n)))))))

(defn lane?
  "Whether symbol `sym` is EDITED AND DRAWN as a lane: its clips as blocks on
  one row, following one another in time and claiming it from each other.

  A DISPLAY HINT AND NOT A TYPE. Nothing in evaluation reads it, `problems` does
  not check it, and a symbol carrying it behaves identically on the stage — it
  says how the timeline draws the symbol and, because the editing rules follow
  the mode, which rules an edge drag inside it plays by. It is on the symbol
  rather than in editor state so that those rules are reproducible between two
  people looking at one document, and it is a field rather than something derived
  from \"the clips do not currently overlap\" because a symbol must not stop being
  a lane the moment something overlaps — that is when the rules are needed.

  Editing and presentation may read it; evaluation may not. See
  `docs/lane-is-a-view-plan.md`."
  [sym]
  (= :lane (:display sym)))

(defn overlaps
  "The pairs of `sym`'s clips that are on screen over the same frames, as
  `[[a b] ...]` of ids. Empty for a symbol whose clips form a sequence.

  A BUG REPORT, NOT A CONDITION TO DESIGN AROUND. In lane mode this cannot
  happen: placing claims time, so anything placed, moved or grown over occupied
  frames TRIMS what it lands on, and `span/finish` — the one commit path for
  every sequence command — refuses rather than committing one. So an overlap that
  appears anyway is a defect in a command.

  Which is why it is NOT `clip/problems`, which means the document will not load:
  a display hint must never be able to stop a document loading, and a document
  that somehow arrives holding an overlap still opens and is drawn visibly wrong.
  Nor `clip/conflicts`, which means a person has a decision to make. This is
  neither."
  [sym]
  (let [spans (for [n (children (:nodes sym))
                    :let [[lo hi] (node/placed-span n)]
                    :when (and (node/finite-number? lo) (node/finite-number? hi))]
                [lo hi (:id n)])]
    (vec (for [[[_ b x] [c _ y]] (partition 2 1 (sort-by (juxt first second str) spans))
               :when (> b c)]
           [x y]))))

(defn order
  "Node ids in topological order: every node after its parent.

  Sorting by parent depth is enough — it does not need Kahn's algorithm, because
  the only edge is parent, and a node's depth is by definition greater than its
  parent's. Ties are broken by id so the order is deterministic across runs,
  which matters because the draw-order sort below falls back on this position."
  [nodes]
  (vec (sort-by (juxt #(depth nodes %) str) (keys nodes))))

(defn z-path
  "The node's z index and every ancestor's, root first.

  Draw order is depth-first by sibling z, so the key that sorts it is the chain
  of z values from the root. A parent's path is a PREFIX of its child's, which is
  why a parent draws before its children without that being a special case.

  `:z` values are fractional-index STRINGS (\"a1\", \"a3\") and compare
  lexicographically, so a node can always be inserted between two siblings
  without renumbering either."
  [nodes id]
  (mapv #(:z (get nodes %)) (rseq (lineage nodes id))))

(defn z-between
  "A `:z` that sorts strictly between `a` and `b`, which must be in order; nil
  for either is no bound on that side. What makes restacking one write.

  The midpoint of the first character they differ in, when there is room. When
  there is not, anything that starts with `a` and is longer sorts after it, and
  before `b` too unless `a` is a prefix of `b` — and then the room is found one
  character further into `b`. \"0\" is the floor, and nothing this makes ends
  in it, so there is always a further character to go to; only an authored key
  ending in \"0\" leaves none, and then one character less than it does."
  [a b]
  (let [a (or a "")]
    (if (nil? b)
      (str a "m")
      (let [i   (count (take-while true? (map = a b)))
            hi  (.charCodeAt b i)
            lo  (if (< i (count a)) (.charCodeAt a i) 48)
            mid (quot (+ lo hi) 2)]
        (cond
          (> mid lo)      (str (subs b 0 i) (char mid))
          (< i (count a)) (str a "m")
          (< (inc i) (count b)) (str (subs b 0 (inc i)) (z-between nil (subs b (inc i))))
          :else           (str (subs b 0 i) (char (dec hi)) "m"))))))

(defn- z-lex
  "Lexicographic compare of two z paths, a prefix sorting first.

  `compare` on vectors will not do: it compares COUNT first, so a deep
  descendant of \"a1\" would sort after a shallow \"a2\" and a painted cel would
  jump in front of the head that carries it.

  `map` over two collections stops at the shorter and `first` short-circuits at
  the first difference, so this walks no further than it has to."
  [a b]
  (or (first (remove zero? (map compare a b)))
      (- (count a) (count b))))

(defn draw-rank
  "id -> its position in draw order.

  Computed ONCE. Draw order is a function of the z paths, which are structural —
  they change when the symbol changes and never because the playhead moved — so
  sorting ops by z on every frame was re-deriving a constant thirty times a
  second. Here it is derived when the symbol is, and a frame sorts small integers.

  `sort-by` is stable and `ord` is topological, so nodes sharing a z path keep
  parent-before-child order without a tiebreak field on every op."
  [nodes ord]
  (let [paths (into {} (map (juxt identity #(z-path nodes %))) ord)]
    (into {} (map-indexed (fn [i id] [id i])) (sort-by paths z-lex ord))))

;; ---------------------------------------------------------------------------
;; colour

(defn colour-index
  "Tone keyword -> the index the raster writes, in a given palette.

  `palette` is a map of tone -> index. It is a PARAMETER, not a global: a tone
  names which mark this is, and which ramp it is read in belongs to the symbol
  the node sits in, so resolution cannot reach for one ambient answer. Today
  there is one palette and it is passed in anyway; when symbols carry a
  `:palette` channel, the walk carries the palette in scope exactly as it already
  carries the parent transform and the local frame.

  An unknown tone resolves to 255, which the palette expansion renders MAGENTA.
  Loud rather than fatal, and the same choice raster/->rgba already makes:
  naming a colour the ramp does not have is a bug in authored data, and it should
  be impossible to miss and should not take the frame down."
  [palette k]
  (cond
    (fn? palette) (palette k)
    (number? k)  k
    (nil? k)     255
    :else        (get palette k 255)))

(defn knockout?
  "Is colour `c` a KNOCKOUT rather than a tone? `:clear` clears every colour
  beneath it in its symbol, `[:clear tone]` clears that tone only. See
  `raster/plot!`."
  [c]
  (or (= :clear c) (and (vector? c) (= :clear (first c)))))

(defn remap?
  "Is colour `c` a REMAP: `[:remap {from to …}]`, slots of the palette in
  scope? It draws nothing of its own; what is already under it is drawn in
  other slots — a flashlight is a circle of this. See `raster/plot!`."
  [c]
  (and (vector? c) (= :remap (first c))))

(defn- knock-index [palette c]
  (if (= :clear c) -1 (colour-index palette (second c))))

(defn lut
  "Remap `[:remap m]` as a raster index -> index table in `palette`. Slots of
  any other palette are left as they are."
  [palette [_ m]]
  (let [t (js/Uint8Array. 256)]
    (dotimes [i 256] (aset t i i))
    (doseq [[a b] m] (aset t (colour-index palette a) (colour-index palette b)))
    t))

(defn knocks?
  "Does any node in `nodes` knock out, on any frame? Such a symbol is drawn into
  a layer of its own."
  [nodes]
  (some (fn [[_ n]]
          (let [c (get-in n [:channels [:style :color]])]
            (some knockout? (cons (:value c) (vals (:keys c))))))
        nodes))

;; ---------------------------------------------------------------------------
;; the walk

(defn- in-span?
  "`:span` is Lottie's ip/op and Flash's PlaceObject/RemoveObject: the range over
  which the node EXISTS, tested in the PARENT's frame space and therefore before
  the node's own time map runs — an instance's own-time span is mapped out by
  `node/placed-span`. Distinct from `[:vis]`, which blinks an existing node on and
  off. Half-open, so two adjacent spans do not both own a frame."
  [n f]
  (if-let [[in out] (node/placed-span n)]
    (and (>= f in) (< f out))
    true))

(defn- finish
  "Resolve stencils, then sort into draw order.

  A stencil is a COLOUR KEY, not a node reference: it is the take format's
  `clip=`, and the indexed buffer being its own clip mask is what keeps the iris
  inside the eye at any gaze and any radius without a per-part mask. So the
  stencil node's own colour is looked up here, after the walk, because the
  stencil may sit anywhere in the order. Two nodes sharing a palette entry share
  a stencil, which is inherent to the technique rather than a defect in it.

  A node stencilled by something that drew NOTHING is DROPPED, not drawn
  unclipped: unclipped would be an iris floating over the cheek on exactly the
  frames where the eye is missing."
  [rank ops]
  (let [by-id (into {} (map (juxt :node :color)) ops)]
    (->> ops
         (keep (fn [op]
                 (if-let [s (:stencil op)]
                   (when-let [idx (get by-id s)]
                     (assoc op :stencil idx))
                   op)))
         (sort-by (comp rank :node))
         vec)))

(defn- n-points
  "Points in a flat [x0 y0 x1 y1 …] value, authored vector or dense view alike."
  [pts]
  (quot (if (vector? pts) (count pts) (.-length pts)) 2))

(defn- xform-at
  "The five transform components at the node's local frame, or nil when any of
  them has no value on it.

  The pivot is read like the rest and is not special-cased to a default here:
  `node/channels` has already filled in `[0 0]` for a node that stores none, and
  a node whose pivot is absent on a frame it is otherwise on has the same nothing
  to be drawn at as one whose position is."
  [rd]
  (let [pos (rd [:xform :pos])
        piv (rd [:xform :pivot])
        rot (rd [:xform :rot])
        scl (rd [:xform :scale])
        skw (rd [:xform :skew])]
    (when-not (or (ch/nothing? pos) (ch/nothing? piv) (ch/nothing? rot)
                  (ch/nothing? scl) (ch/nothing? skw))
      [pos piv rot scl skw])))

(defn- visible?
  "Is the node switched on this frame?

  `[:vis]` IS A BOOLEAN, and this insists on it rather than testing truthiness,
  because the two obvious implementations are both wrong about a DENSE `[:vis]`.
  A dense block yields 0 or 1, and 0 is TRUTHY in CLJS — so `(if v …)` shows a
  hidden frame, and `(true? v)` hides every frame. Neither reads as an error.

  docs/animation-model.md's parts table says `:mouth-in` carries `[:vis]` dense;
  flow/freeze writes it KEYED, because a threshold crossing is a handful of
  transitions and hold is the default, and because a human has to be able to fix
  one frame of it. When something does want a dense one it will land here loudly
  instead of blanking the symbol.

  Absence is not a boolean and is not an error: a subject that is not on the
  frame has nothing to show."
  [id v]
  (cond
    (true? v)       true
    (false? v)      false
    (ch/nothing? v) false
    :else (throw (ex-info "[:vis] must sample to a boolean"
                          {:node id :value v}))))

(defn- place
  "Where a node sits this frame, as {:m world :f local-frame :rd reader}, or nil
  when it is not on the frame at all.

  Three gates, and nil from any of them removes the node's DESCENDANTS too,
  which is why this is one answer rather than three flags: a node outside its
  span does not exist, a switched-off feature takes its parts with it, and a node
  with no transform gives its children nowhere to be.

  A missing [:geom :pts] is deliberately NOT one of them — that is `emit`'s
  business. An absent mouth outline has nothing to draw, but the head it hangs
  off is still exactly where it was, and that asymmetry is the whole reason
  presence is tracked per channel rather than per node."
  [{:keys [read mat-for pinv-for scratch]} n parent f pre]
  (let [pf  (if parent (:f parent) f)
        ;; Where the PREVIOUS grid slot landed, carried down the same path as `f`
        ;; through the same time maps. `(:pre parent)` rather than a second walk,
        ;; so an exposure fold or a retime on an ancestor is in it already — which
        ;; is what makes a held exposure's interval come out right without the snap
        ;; knowing exposure exists.
        ppf (if parent (:pre parent) pre)]
    (when (in-span? n pf)
      (let [id  (:id n)
            chs (node/channels n)
            lf  (node/local-frame n pf)
            plf (node/local-frame n ppf)
            rd  (fn [path] (read id path (get chs path) lf plf))]
        (when (visible? id (rd [:vis]))
          (when-let [[pos piv rot scl skw] (xform-at rd)]
            ;; dest aliases `local` here, which mul! allows: it reads both
            ;; operands fully before writing either.
            (let [m (node/local! (mat-for id) pos piv rot scl skw)]
              {:m   (node/world! m (:m parent) (pinv-for id) m scratch)
               :f   lf
               :pre plf
               :rd  rd})))))))

(defn- emit
  "Emit geometry in the symbol's space. Rect sizes stay fractional until
  rasterization, so enclosing symbol transforms can still scale them."
  [{:keys [palette buf-for]} n {:keys [m rd]} base]
  ;; Read only by the kinds that have a colour: a group or an instance has no
  ;; `[:style :color]` to read.
  (let [paint  (fn [op]
                 (let [c (rd [:style :color])]
                   (cond
                     (knockout? c) (assoc op :knock (knock-index palette c) :color 0)
                     (remap? c)    (assoc op :lut (lut palette c) :color 0)
                     :else         (assoc op :color (colour-index palette c)))))]
    (case (:kind n)
      :group nil
      :instance nil

      :poly
      (let [pts (rd [:geom :pts])]
        (when-not (ch/nothing? pts)
          (let [np  (n-points pts)
                out (buf-for (:id n) np)]
            (dotimes [k np]
              (node/apply-pt! out k m
                              (ch/component pts (* 2 k))
                              (ch/component pts (inc (* 2 k)))))
            (paint (assoc base :kind :poly :pts out :n np)))))

      :disc
      (let [rad (rd [:geom :radius])]
        (when-not (ch/nothing? rad)
          (paint (assoc base :kind :disc
                        :cx (aget m 4) :cy (aget m 5)
                        :r (* rad (node/mean-scale m))))))

      :rect
      (let [size (rd [:geom :size])]
        (when-not (ch/nothing? size)
          (paint (assoc base :kind :rect
                        :cx (aget m 4) :cy (aget m 5)
                        :size (* size (node/mean-scale m))))))

      (throw (ex-info "node kind is not implemented"
                      {:node (:id n) :kind (:kind n)})))))

(defn- nodes-of
  "The symbol's node map, REFUSING a map that has none.

  A clip and a symbol both have an `:id` and both are maps, so handing a CLIP to
  an evaluator is the one mistake this type split makes easy — and the result is
  not an error, it is `(:nodes clip)` being nil and a frame resolving to no ops at
  all. That reads as a black stage, or, in a benchmark, as \"0 nodes\" and a
  flattering number. It happened once while the split was being made, which is why
  this is a guard and not a comment.

  Sounds are left out: they are heard, not drawn, and have no transform to
  evaluate. `audio/mix` is what plays them."
  [sym]
  (let [nodes (:nodes sym)]
    (when-not (map? nodes)
      (throw (ex-info (str "not a symbol: :nodes is " (pr-str nodes)
                           " — a clip is not a symbol, its `:symbols` hold them")
                      {:keys (vec (sort-by str (keys sym)))})))
    (into {} (remove #(= :audio (:kind (val %)))) nodes)))

(defn- base-channel-frame
  "`:reads` holds the frames a node's own channels read; marked channels read
  instance pose choices, and the preserve-snap where nobody has made one.

  `lf` is this slot's local frame and `plf` the PREVIOUS slot's, both already
  through placement and retime, so `(plf, lf]` is the interval this slot is the
  first to cover — the frames the grid shows to nobody. That is the only thing the
  snap needs from the grid, and it is why the pair is threaded this far down
  instead of the snap happening where the grid becomes a native frame: the snap is
  per pose group, and a group is a fact that only exists here."
  [choices marks reads nodes id c lf plf]
  (cond
    (contains? reads id)
    (node/hold (js/Math.floor lf) (get reads id))

    (:pose-sampled? c)
    (let [group (or (:pose-group (get nodes id)) id)]
      (pose/source-frame choices
                         (if (contains? choices [:node id]) [:node id] group)
                         lf
                         ;; THE SNAP IS THE DEFAULT POSE. `source-frame` reaches a
                         ;; default only where the hand has said nothing, so seating
                         ;; it here leaves an explicit cut beating a snap for free —
                         ;; manual precedence is absolute — and costs no plumbing on
                         ;; the pose side, per-group choices being threaded already.
                         (pose/snapped-frame marks group
                                             (js/Math.floor plf)
                                             (js/Math.floor lf))))

    :else (js/Math.floor lf)))

(defn- holds-read
  "The frames node `n` reads its OWN channels at, held — not its children's,
  which is what makes this different from `:time :holds` — or nil for every
  frame. `{:holds [0]}` is a face's head at its start, and `{:holds-of :plate}`
  is the head holding wherever its footage holds, which is one list of frames
  read by two nodes rather than two lists kept in step."
  [nodes n]
  (let [{:keys [holds holds-of]} (:reads n)
        hs (if holds-of (get-in nodes [holds-of :time :holds]) holds)]
    (when (seq hs) hs)))

(defn- prepared-reads [nodes]
  (into {} (keep (fn [[id n]] (some->> (holds-read nodes n) (vector id)))) nodes))

(defn- eval-into
  "One frame, as a fold over the nodes in topological order.

  `ctx` carries how a channel is read and where its points are written:

    :read      (fn [id path channel local-frame] -> v)
    :palette   tone -> index, the ramp in scope
    :mat-for   (fn [id] -> Float64Array)      the node's world transform
    :pinv-for  (fn [id] -> Float64Array|nil)  its parent-inverse
    :buf-for   (fn [id n-points] -> Float64Array)
    :scratch   one spare 6-element matrix"
  [ctx nodes ord rank f pre]
  (-> (reduce
       (fn [{:keys [placed ops] :as acc} id]
         (let [n      (get nodes id)
               pid    (:parent n)
               parent (when pid (get placed pid))]
           ;; A node whose parent was dropped is dropped with it, and so is
           ;; everything under it. Topological order is what makes that one
           ;; lookup instead of a subtree walk.
           (if (and pid (nil? parent))
             acc
             (if-let [p (place ctx n parent f pre)]
               (let [_  (when-let [on-place (:on-place ctx)] (on-place id p))
                     op (emit ctx n p {:node id :stencil (:stencil n)})]
                 (cond-> (update acc :placed assoc id p)
                   op (update :ops conj op)))
               acc))))
       {:placed {} :ops []}
       ord)
      :ops
      (->> (finish rank))))

;; ---------------------------------------------------------------------------
;; the specification

(defn eval-frame
  "Symbol at frame f -> draw ops in z order. Pure, and allocates freely.

  `f` is in THIS symbol's frame space. For the symbol on screen that is the
  transport's frame; inside an instance it is the instance's own space, and the instance boundary is
  the only place the space changes.

  `pre` is where the grid slot BEFORE this one landed, which with `f` is the
  interval the preserve-snap may reach back into — see `pose/snapped-frame`. The
  shorter arity means one native frame per slot, so the interval is `f` alone and
  nothing snaps: that is what a caller rendering a native frame directly is asking
  for, and it is what keeps this evaluator and `resolver` the same answer.

  This is the definition of what a frame means. `resolver` is what plays it."
  ([sym f store palette opts] (eval-frame sym f (dec f) store palette opts))
  ([sym f pre store palette {:keys [pose-tracks snap]}]
   (let [nodes (nodes-of sym)
         choices (pose/prepare pose-tracks)
         marks   (when (and snap (snap (:id sym)))
                   (pose/marks nodes (:frames sym) store))
         reads   (prepared-reads nodes)
         ord   (order nodes)]
     (eval-into {:read     (fn [id path c lf plf]
                             (ch/value-at c (base-channel-frame choices marks reads
                                                                nodes id c lf plf)
                                          lf store))
                 :palette  palette
                 :mat-for  (fn [_id] (node/mat))
                 :pinv-for (fn [id] (node/pinv (get nodes id)))
                 :buf-for  (fn [_id n] (js/Float64Array. (* 2 n)))
                 :scratch  (node/mat)}
                nodes ord (draw-rank nodes ord) f pre))))

;; ---------------------------------------------------------------------------
;; the playback path

(defn- point-capacity
  "How many points the widest value of a [:geom :pts] channel holds.

  FIXED TOPOLOGY is what makes this a number at all: every key of a part carries
  the same vertex count with the same vertex meanings, so the buffer can be
  allocated once. A variable vertex count would force a per-frame offset table
  and a scan, which is why the aesthetic constraint is a performance asset rather
  than a cost."
  [c]
  (quot (cond
          (:dense c) (:stride (:dense c))
          (:animated? c) (transduce (map #(if (vector? %) (count %) (.-length %)))
                                    max 0 (vals (:keys c)))
          :else (let [v (:value c)] (if (vector? v) (count v) (.-length v))))
        2))

(defprotocol IResolver
  (world-of [this id]
    "The node's world transform AS OF THE LAST FRAME RESOLVED, or nil if it was
    not placed on that frame.

    The matrices are the ones evaluation mutates in place, so this is a read of
    live state rather than a snapshot — which is exactly what the caller wants.
    A registered photo underlay has to ride the same transform the vectors went
    through or it is merely decorative, and it paints immediately after the frame
    it belongs to, so \"as of the last frame\" is the only answer that can be
    correct.")
  (frame-of [this id] "The placed node's local frame on the last resolve.")
  (pre-frame-of [this id]
    "The same node's local frame for the grid slot BEFORE the last resolve.

    Half of the preserve-snap's interval, and the half only the walk can answer:
    the mapping from a grid slot to a node's local frame runs through every time
    map between them, so the frame the previous slot landed on is what the walk
    carried, not something a caller can recompute from the slot index."))

(defn resolver
  "(fn [f] -> ops). Holds everything that does not change per frame.

  The point buffers are REUSED between frames, so a caller must consume the ops
  before asking for the next frame. That is the contract the rAF loop wants
  anyway — it reads, blits, and dispatches nothing — and it is what makes a frame
  cost a lookup and a blit rather than an allocation per vertex.

  The op maps themselves are allocated fresh, and deliberately: there are a dozen
  of them per frame against hundreds of points, so pooling them would buy
  nothing and cost the ability to hand an op list around as plain data.

  `store` and `palette` are POSITIONAL because neither is optional: a dense
  channel cannot be read without the store it names, and every op carries a
  colour index. `opts` is a map because the rest genuinely are optional, and
  because a fifth of them later is then a key rather than a nil at every one of
  these call sites — which is what the arity ladder that used to be here was
  standing in for."
  [sym store palette {:keys [pose-tracks snap]}]
   (let [nodes   (nodes-of sym)
         choices (pose/prepare pose-tracks)
         ;; NO MARKS IS THE OFF STATE, and that it needs no second code path is
         ;; the reason `snapped-frame` falls back to the slot's own default rather
         ;; than being asked whether it should. Off is the cadence alone, which is
         ;; what ships today, so it is also the DEFAULT: an opts map that says
         ;; nothing gets the behaviour it got before the snap existed.
         ;;
         ;; `:snap` IS ASKED ABOUT THIS SYMBOL, not the resolver, because the marks
         ;; are the FACE'S: the closure cuts are stored on its own nodes, so every
         ;; placement of one face has the same ones and a per-placement answer would
         ;; be a setting with nothing in it. A set of symbol ids is the usual
         ;; argument, and any predicate on one will do.
         ;;
         ;; One option, and it is the snap's alone. The plate side will never want
         ;; one — its proposal is materialised into the plate's `:time :holds` rather than
         ;; computed on the render path — so this is not half of a pair.
         marks   (when (and snap (snap (:id sym)))
                   (pose/marks nodes (:frames sym) store))
         reads   (prepared-reads nodes)
         ord     (order nodes)
         rank    (draw-rank nodes ord)
         cursors (into {}
                       (map (fn [id]
                              [id (into {} (map (fn [[p c]] [p (ch/cursor c store)]))
                                        (node/channels (get nodes id)))]))
                       ord)
         mats    (into {} (map (fn [id] [id (node/mat)])) ord)
         pinvs   (into {} (keep (fn [id] (when-let [p (node/pinv (get nodes id))] [id p]))) ord)
         bufs    (into {}
                       (keep (fn [id]
                               (when-let [c (get-in nodes [id :channels [:geom :pts]])]
                                 [id (js/Float64Array. (* 2 (point-capacity c)))])))
                       ord)
         scratch (node/mat)
         ;; Every call to mat-for is a placement: eval-into reaches it only after
         ;; the span, visibility and transform gates have all passed. So wrapping
         ;; it is how the resolver learns which nodes exist this frame without
         ;; eval-into having to report it — and it covers groups, which are
         ;; placed but emit no op, and which are exactly what an underlay rides.
         placed  (volatile! {})
         ctx     {:read     (fn [id path c lf plf]
                              (when-let [cursor (get-in cursors [id path])]
                                (ch/sample! cursor
                                            (base-channel-frame choices marks reads
                                                                nodes id c lf plf)
                                            lf)))
                  :palette  palette
                  :mat-for  (fn [id] (get mats id))
                  :on-place (fn [id p] (vswap! placed assoc id p))
                  :pinv-for (fn [id] (get pinvs id))
                  :buf-for  (fn [id _n] (get bufs id))
                  :scratch  scratch}
         step    (fn [f pre]
                   (vreset! placed {})
                   (eval-into ctx nodes ord rank f pre))]
     (reify
       IFn
       ;; One frame and no interval is one native frame per slot — see `eval-frame`.
       (-invoke [_ f] (step f (dec f)))
       (-invoke [_ f pre] (step f pre))
       IResolver
       (world-of [_ id] (:m (get @placed id)))
       (frame-of [_ id] (:f (get @placed id)))
       (pre-frame-of [_ id] (:pre (get @placed id))))))

;; ---------------------------------------------------------------------------

(def symbol-keys
  "Every field a symbol may carry, and the reason `arthur.domain.leaf` refuses
  one it does not know: a field added without a leaf to save it in is a field that
  saves silently and comes back missing.

  `:palette` is in the vocabulary and nothing writes one yet. A symbol is where
  a ramp belongs — `domain/symbol` takes the palette as a PARAMETER rather than
  reaching for a global precisely so that a nested symbol can carry its own —
  and leaving the field out would make the first one a migration instead of a
  write.

  `:name` is what a person calls it, and is not its id: an id is what instances
  and saved leaves point at, so renaming a symbol must not change it.

  `:width` and `:height` are the symbol's own stage, and are absent until someone
  sets them: a symbol without them uses the clip's — see `clip/stage`.

  `:display` is how the TIMELINE draws the symbol — `:lane` for its clips as
  blocks on one row — and is saved because two people editing one document must
  play by the same editing rules. See `lane?`.

  `:media` is what a `:type :trace` symbol shows — `{:footage id :range [in
  out]}` or `{:image sha256}` — and such a symbol has no nodes: its frames are
  the footage's, played in real time by the speed of its placement, and its
  `:width`/`:height` its pixels. A still shows the same picture on every one of
  its frames, so how many it has is only how long it was made to last, and
  trimming its placement is how that changes. See `clip/trace-op`."
  #{:id :name :frames :width :height :nodes :palette :palette-track
    :palette-channel :type :palette-ref :display :media})

(defn- media-problems
  "Why `sym`, a tracing symbol, does not say what it shows. Empty when it does."
  [{:keys [media frames width height nodes]}]
  (let [{:keys [footage image range]} media]
    (cond-> []
      (seq nodes)
      (conj "a tracing symbol cannot contain nodes")
      (not (= 1 (count (filter some? [footage image]))))
      (conj ":media must name exactly one of :footage or :image")
      (and footage (not (and (vector? range) (= 2 (count range))
                             (every? integer? range) (apply < range)
                             (= frames (- (second range) (first range))))))
      (conj "a footage :media needs a [in out) :range as long as the symbol's :frames")
      (not (and (integer? frames) (pos? frames)))
      (conj "a tracing symbol is a positive whole number of frames")
      (not (and (pos? width) (pos? height)))
      (conj "a tracing symbol needs its media's pixel :width and :height"))))

(defn problems
  "Human-readable reasons this symbol will not evaluate. Empty means it will.

  Node structure only. The tracking identities — subjects, features, groups — are
  the CLIP's and are checked by `arthur.domain.clip/problems`, which is not a
  layering nicety: a feature names nodes, and a library symbol's nodes are not
  the ones a face was tracked into.

  Total by construction — it reports a cycle rather than looping on one — because
  its whole job is to be safe to run over authored data before that data is
  trusted."
  [sym]
  (let [nodes (:nodes sym)]
    (if-not (map? nodes)
      [":nodes must be a map of id -> node"]
      (-> []
          (cond->
            (and (= :palette (:type sym)) (seq nodes))
            (conj "a palette symbol cannot contain nodes"))
          (into (when (= :trace (:type sym)) (media-problems sym)))
          (into (for [[id n] nodes
                      :when  (not= id (:id n))]
                  (str "node under key " (pr-str id) " has :id " (pr-str (:id n)))))
          (into (for [[id n] nodes
                      :when  (and (:parent n) (not (contains? nodes (:parent n))))]
                  (str "node " (pr-str id) " has :parent " (pr-str (:parent n))
                       " which is not in the symbol")))
          (into (for [[id n] nodes
                      :when  (and (:stencil n) (not (contains? nodes (:stencil n))))]
                  (str "node " (pr-str id) " has :stencil " (pr-str (:stencil n))
                       " which is not in the symbol")))
          (into (for [[id n] nodes
                      p      (node/problems n)]
                  (str "node " (pr-str id) ": " p)))
          (into (for [[id n] nodes
                      :when (some? (:trace n))]
                  (str "node " (pr-str id) ": :trace is gone — trace keys are the "
                       "footage placement's :time :holds, and a head follows them "
                       "with :reads {:holds-of …}; make the face again from its footage")))
          ;; `:reads` names its holds or another node's, the way `:stencil` names
          ;; a node: one that is here, and never one whose frame depends on the
          ;; reader's own channels being read first.
          (into (for [[id n] nodes
                      :let [{:keys [holds holds-of] :as r} (:reads n)]
                      :when (some? r)
                      p (cond
                          (and holds holds-of)
                          [":reads names its own :holds or a node's, not both"]
                          holds-of
                          (cond
                            (not (contains? nodes holds-of))
                            [(str ":reads :holds-of " (pr-str holds-of) " is not in the symbol")]
                            (some #{holds-of} (lineage nodes id))
                            [":reads :holds-of names the node itself or one above it"])
                          :else
                          (when-not (and (vector? holds) (every? node/finite-number? holds)
                                         (or (empty? holds) (apply < holds)))
                            [":reads :holds must be a vector of increasing frames"]))]
                  (str "node " (pr-str id) ": " p)))
          (into (for [k (remove symbol-keys (keys sym))]
                  (str "symbol has a field with no leaf to save it in: " (pr-str k))))
          (into (when-not (or (nil? (:frames sym)) (and (integer? (:frames sym)) (pos? (:frames sym))))
                  [(str ":frames is " (pr-str (:frames sym))
                        " — a symbol is a frame SPACE, so its length is a positive integer")]))
          (into (for [k [:width :height]
                      :let [v (get sym k)]
                      :when (and (some? v) (not (and (integer? v) (pos? v))))]
                  (str k " is " (pr-str v) " — a symbol stage dimension must be a positive integer")))
          (into (try
                  (doall (map #(depth nodes %) (keys nodes)))
                  nil
                  (catch :default e [(ex-message e)])))))))
